/**
 * Detecção automática do host que executa o totem.
 *
 * O bundle é o mesmo nas duas plataformas; quem as diferencia é o objeto que o
 * host injeta no `window` antes de carregar a página:
 *   - Android (WebView do React Native) injeta `window.RNBridge`
 *   - Windows (CefSharp) injeta `window.CefSharp`
 *
 * Ordem de decisão:
 *   1. override manual em localStorage (`@meep/forcePlatform`) — para debug em campo
 *   2. host presente no `window` — fonte da verdade
 *   3. `REACT_APP_NATIVE` — só alcança o navegador em desenvolvimento, onde
 *      nenhum dos dois hosts existe
 *   4. userAgent Android
 *   5. padrão: CefSharp
 */
export type TotemPlatform = "native" | "cefsharp";

const FORCE_PLATFORM_KEY = "@meep/forcePlatform";

const hasRNBridge = (): boolean =>
  typeof window !== "undefined" && "RNBridge" in window && !!window.RNBridge;

const hasCefSharp = (): boolean =>
  typeof window !== "undefined" &&
  typeof (window as { CefSharp?: unknown }).CefSharp !== "undefined";

const forcedPlatform = (): TotemPlatform | null => {
  try {
    const value = localStorage.getItem(FORCE_PLATFORM_KEY)?.trim().toLowerCase();
    if (value === "native" || value === "cefsharp") {
      return value;
    }
  } catch (error) {
    // localStorage pode estar indisponível (WebView restrita / modo privado)
  }
  return null;
};

const platformFromEnv = (): TotemPlatform | null => {
  const value = process.env.REACT_APP_NATIVE?.trim().toLowerCase();
  if (value === "true" || value === "1") {
    return "native";
  }
  if (value === "false" || value === "0") {
    return "cefsharp";
  }
  return null;
};

const detectPlatform = (): TotemPlatform => {
  const forced = forcedPlatform();
  if (forced) {
    return forced;
  }
  if (hasRNBridge()) {
    return "native";
  }
  if (hasCefSharp()) {
    return "cefsharp";
  }
  const fromEnv = platformFromEnv();
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof navigator !== "undefined" && /android/i.test(navigator.userAgent)) {
    return "native";
  }
  return "cefsharp";
};

let cachedPlatform: TotemPlatform | null = null;

export const getPlatform = (): TotemPlatform => {
  if (cachedPlatform === null) {
    cachedPlatform = detectPlatform();
    if (typeof window !== "undefined") {
      // Global de diagnóstico: dá para conferir a plataforma pelo console do
      // host (`window.meepPlatform`) sem precisar de log.
      window.meepPlatform = cachedPlatform;
      window.isReactNative = cachedPlatform === "native";
    }
    console.log("[platform] host detectado:", cachedPlatform, {
      rnBridge: hasRNBridge(),
      cefSharp: hasCefSharp(),
      forced: forcedPlatform(),
      env: process.env.REACT_APP_NATIVE,
    });
  }
  return cachedPlatform;
};

export const isNativeApp = (): boolean => getPlatform() === "native";

export const isCefSharpApp = (): boolean => getPlatform() === "cefsharp";

/**
 * Reavalia a detecção. Útil se o host injetar o objeto depois do bundle carregar
 * ou ao trocar o override manual.
 */
export const refreshPlatform = (): TotemPlatform => {
  cachedPlatform = null;
  return getPlatform();
};

/** Override manual (debug). `null` volta para a detecção automática. */
export const setForcedPlatform = (platform: TotemPlatform | null): void => {
  try {
    if (platform) {
      localStorage.setItem(FORCE_PLATFORM_KEY, platform);
    } else {
      localStorage.removeItem(FORCE_PLATFORM_KEY);
    }
  } catch (error) {
    console.warn("[platform] não foi possível gravar o override", error);
  }
  refreshPlatform();
};
